import { useEffect, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useDeleteUser } from '../../hooks/useAdmin';

interface ArchiveUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: { id: number; firstName: string | null; lastName: string | null } | null;
  defaultDriveTransferTo?: string;
  onArchived?: () => void;
}

export function ArchiveUserModal({ isOpen, onClose, user, defaultDriveTransferTo = '', onArchived }: ArchiveUserModalProps) {
  const deleteMutation = useDeleteUser();
  const [skipOffboarding, setSkipOffboarding] = useState(false);
  const [driveTransferTo, setDriveTransferTo] = useState(defaultDriveTransferTo);

  // Reset form + stale mutation error each time the modal (re)opens so a new
  // target user doesn't inherit the previous attempt's input or error banner.
  useEffect(() => {
    if (isOpen) {
      setSkipOffboarding(false);
      setDriveTransferTo(defaultDriveTransferTo);
      deleteMutation.reset();
    }
    // deleteMutation is stable across renders; only re-run on open/default change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDriveTransferTo]);

  const handleArchive = async () => {
    if (!user) return;
    // F21-002: wrap mutateAsync in try/catch so a rejection (network blip,
    // 409 conflict, offboarding-config error) doesn't bubble up as an
    // unhandledrejection. Modal stays open on failure; React Query exposes
    // the message via `deleteMutation.error` for the inline banner below.
    try {
      await deleteMutation.mutateAsync({
        id: user.id,
        options: skipOffboarding ? { skipOffboarding: true } : { skipOffboarding: false, driveTransferTo },
      });
      onArchived?.();
      onClose();
    } catch {
      // mutation error handled by React Query — banner renders below
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Archive User"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleArchive}
            isLoading={deleteMutation.isPending}
            disabled={!skipOffboarding && !driveTransferTo.trim()}
          >
            Archive User
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          This will archive {user?.firstName} {user?.lastName} and trigger the offboarding workflow.
        </p>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipOffboarding}
            onChange={(e) => setSkipOffboarding(e.target.checked)}
            className="rounded border-gray-300"
          />
          Skip offboarding workflow
        </label>
        {!skipOffboarding && (
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">Transfer Google Drive files to</label>
            <input
              type="email"
              value={driveTransferTo}
              onChange={(e) => setDriveTransferTo(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        {deleteMutation.isError && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {(deleteMutation.error as Error | null)?.message || 'Failed to archive user. Please try again.'}
          </div>
        )}
      </div>
    </Modal>
  );
}
